import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { MaterialIcon } from '@lapo/ui';
import type { ApiClient, ContactDTO } from './auth/apiClient.js';
import { DecisionsSection } from './DecisionsSection.js';
import { RelationshipSection } from './RelationshipSection.js';

/**
 * Enh-B — a focused Contact detail surface (`#/contacts/:id`). Header (avatar, name, org/title, emails)
 * plus the reverse-surfaced "Decisions involving this person" section (D24), each row linking into the
 * Decision Ledger. There is no single-contact endpoint, so we resolve the contact from the list.
 */
const AVATAR_COLORS = ['#2563eb', '#0891b2', '#7c3aed', '#db2777', '#ea580c', '#16a34a', '#4f46e5', '#b91c1c', '#0d9488', '#9333ea'];
function colorFor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}
function initials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 1).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function ContactDetailView({
  api,
  contactId,
  onBack,
  onOpenDecision,
}: {
  api: ApiClient;
  contactId: string;
  onBack: () => void;
  onOpenDecision: (decisionId: string) => void;
}): ReactElement {
  const [contact, setContact] = useState<ContactDTO | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    void api
      .getContacts()
      .then((all) => {
        if (live) setContact(all.find((c) => c.contactId === contactId) ?? null);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [api, contactId]);

  const query = useMemo(() => ({ participantContactId: contactId }), [contactId]);
  const name = contact?.displayName || contact?.emails[0] || contact?.org || contactId;
  const sub = [contact?.title, contact?.org].filter((s): s is string => !!s && s !== name).join(' · ');

  return (
    <div className="lapo-contactdetail">
      <button type="button" className="lapo-contactdetail-back" onClick={onBack}>
        <MaterialIcon name="arrow_back" size={18} /> Contacts
      </button>

      {loading && !contact ? (
        <p className="lapo-decisions-empty">Loading…</p>
      ) : !contact ? (
        <p className="lapo-decisions-empty">Contact not found.</p>
      ) : (
        <>
          <header className="lapo-contactdetail-head">
            {contact.photoUrl ? (
              <img className="lapo-contactdetail-avatar" src={contact.photoUrl} alt="" referrerPolicy="no-referrer" />
            ) : (
              <span className="lapo-contactdetail-avatar lapo-contactdetail-avatar-initial" style={{ backgroundColor: colorFor(contactId) }} aria-hidden>
                {initials(name)}
              </span>
            )}
            <div className="lapo-contactdetail-id">
              <h1 className="lapo-contactdetail-name">{name}</h1>
              {sub && <p className="lapo-contactdetail-sub">{sub}</p>}
              {contact.emails.map((e) => (
                <a key={e} className="lapo-contactdetail-email" href={`mailto:${e}`}>
                  {e}
                </a>
              ))}
            </div>
          </header>

          <RelationshipSection api={api} entityKey={contactId} />
          <DecisionsSection api={api} query={query} onOpen={onOpenDecision} title="Decisions involving this person" />
        </>
      )}
    </div>
  );
}
